import { format, isValid, parseISO } from "date-fns"

/** Group default window passed into Add Volunteer (date part only; may include time in ISO string). */
export type DefaultServePeriod = {
    from: string
    to: string
    regionId?: number
}

/** Calendar day from API `YYYY-MM-DD` or ISO prefix — same as create form defaults (local calendar, no UTC midnight shift). */
export function localDateFromApiDateString(value: string): Date {
    return parseISO(value.trim().slice(0, 10))
}

/** Serve boundary from API (ISO or `YYYY-MM-DD`) or `Date` → `Date` for range inputs (calendar-local, not full-ISO `new Date` shift). */
export function parseServePeriodBoundary(value: unknown): Date | undefined {
    if (value == null || value === "") return undefined
    if (value instanceof Date) {
        return isNaN(value.getTime()) ? undefined : value
    }
    if (typeof value === "string") {
        const t = value.trim()
        if (!t) return undefined
        const d = localDateFromApiDateString(t)
        return isValid(d) ? d : undefined
    }
    return undefined
}

export type ServePeriodFormValue = {
    from: Date
    to: Date
    regionId?: number
}

type ApiServeDate = { from?: string; to?: string; regionId?: number }

/**
 * Normalize API serve dates to Date (same representation as makeDefaultEntry).
 * If defaultServePeriod is set, append it when no existing period matches the same calendar range.
 */
export function mergeServePeriodsForGroupAddForm(
    serveDates: ApiServeDate[] | undefined | null,
    defaultServePeriod?: DefaultServePeriod,
): ServePeriodFormValue[] {
    const periods: ServePeriodFormValue[] = []
    for (const s of serveDates ?? []) {
        if (!s?.from?.trim() || !s?.to?.trim()) continue
        const from = localDateFromApiDateString(s.from)
        const to = localDateFromApiDateString(s.to)
        if (!isValid(from) || !isValid(to)) continue
        let regionId: number | undefined
        if (s.regionId != null && String(s.regionId).trim() !== "") {
            const n = typeof s.regionId === "number" ? s.regionId : parseInt(String(s.regionId), 10)
            regionId = Number.isNaN(n) ? undefined : n
        }
        periods.push({ from, to, regionId })
    }

    if (defaultServePeriod) {
        const defFromKey = defaultServePeriod.from.trim().slice(0, 10)
        const defToKey = defaultServePeriod.to.trim().slice(0, 10)
        const alreadyPresent = periods.some(
            (p) =>
                format(p.from, "yyyy-MM-dd") === defFromKey &&
                format(p.to, "yyyy-MM-dd") === defToKey,
        )
        if (!alreadyPresent) {
            const from = localDateFromApiDateString(defaultServePeriod.from)
            const to = localDateFromApiDateString(defaultServePeriod.to)
            if (isValid(from) && isValid(to)) {
                periods.push({
                    from,
                    to,
                    regionId: defaultServePeriod.regionId,
                })
            }
        }
    }

    return periods
}
